import { Debugger, createDebug } from "../utils/LazyDebug";

const CACHE_VERSION = 1;
const CACHE_NAMES = {
  shopify: `wk-shopify-v${CACHE_VERSION}`,
  storefront: `wk-storefront-v${CACHE_VERSION}`,
  wishlist: `wk-wishlist-v${CACHE_VERSION}`,
};

export type CacheName = keyof typeof CACHE_NAMES;

export interface CachedApiParams {
  name: CacheName;
}

export enum CacheStrategy {
  None = "no-store",
  Short = "public, max-age=1, stale-while-revalidate=10",
  // Short = "public, max-age=15, stale-while-revalidate=120",
  Medium = "public, max-age=60, stale-while-revalidate=86400",
  Long = "public, max-age=3600, stale-while-revalidate=86400",
}

// Cleanup unsed caches
// TODO: Cleanup stale data
const cleanup = () => {
  const debug = createDebug(`wk:cache`);
  const currentCacheNames = Object.values(CACHE_NAMES);

  caches.keys().then((cacheNames) =>
    Promise.all(
      cacheNames.map((cacheName) => {
        if (!currentCacheNames.includes(cacheName)) {
          debug("Delete old cache", { cacheName });
          return caches.delete(cacheName);
        }
      }),
    ),
  );
};

cleanup();

export class CacheApi {
  #cache?: Cache;
  #cachePromise?: Promise<Cache>;
  #pending: Map<
    string,
    { controller: AbortController; promise: Promise<unknown> }
  > = new Map();
  #debug: Debugger;
  #name: string;

  constructor(params: CachedApiParams) {
    this.#name = CACHE_NAMES[params.name];
    this.#debug = createDebug(`wk:cache:${params.name}`);
    this.initCache({ name: this.#name });
  }

  async initCache({ name }: { name: string }) {
    if (this.#cache) return;
    if (this.#cachePromise) return this.#cachePromise;
    try {
      this.#cachePromise = caches.open(name);
      this.#cache = await this.#cachePromise;
      this.#cachePromise = undefined;
      this.#debug("Cache initialised");
    } catch (error) {
      console.warn("Failed to open cache.", error);
    }
  }

  async withCache<T>(
    queryKey: string | readonly unknown[],
    strategy: CacheStrategy,
    fetch: (signal: AbortSignal) => Promise<T>,
  ) {
    if (this.#cachePromise) {
      await this.#cachePromise;
    }
    // await this.initCache({ name: this.#name });

    const cacheKey = this.hashKey(queryKey);

    const executeRemoteFetch = (signal: AbortSignal) =>
      fetch(signal).then(async (value) => {
        this.#debug("Fetched from remote", { cacheKey, strategy, value });
        this.#pending.delete(cacheKey);
        await this.setItemInCache(cacheKey, strategy, value);
        return value;
      });

    const executeFetch = (signal: AbortSignal) =>
      this.getItemInCache(cacheKey).then(async (response) => {
        if (response) {
          const cacheInfo = this.getCacheInfo(response.headers);

          if (cacheInfo.stale && cacheInfo.revalidate) {
            this.#debug("Revalidate while stale", {
              cacheKey,
              strategy,
              cacheInfo,
            });
            this.#pending.delete(cacheKey);
            executeRemoteFetch(signal);
            return response.json().then(({ value }) => value as T);
          }
          if (!cacheInfo.stale) {
            this.#debug("Serve cached value", {
              cacheKey,
              strategy,
              cacheInfo,
            });
            this.#pending.delete(cacheKey);
            return response.json().then(({ value }) => value as T);
          }
        }

        this.#debug("Missed cache", { cacheKey, strategy });
        return executeRemoteFetch(signal);
      });

    const pendingRequest = this.#pending.get(cacheKey);
    if (pendingRequest) return pendingRequest.promise as Promise<T>;

    const controller = new AbortController();
    const promise = executeFetch(controller.signal);
    this.#pending.set(cacheKey, { promise, controller });
    return promise;
  }

  async setItemInCache(key: string, strategy: CacheStrategy, value: unknown) {
    if (!this.#cache) return value;
    if (strategy === CacheStrategy.None) return value;

    const url = `https://wishlistking.dev/${key}`;
    const request = new Request(url);

    const response = new Response(
      JSON.stringify({
        value,
      }),
    );

    response.headers.set("cache-control", strategy);
    response.headers.set("cache-put-date", new Date().toUTCString());

    await this.#cache.put(request, response);

    return value;
  }

  async getItemInCache(key: string) {
    if (!this.#cache) return;

    const url = `https://wishlistking.dev/${key}`;
    const request = new Request(url);

    return this.#cache.match(request);
  }

  getCacheInfo(headers: Headers) {
    const cacheInfo = {
      stale: true,
      revalidate: false,
    };

    const cacheControl = headers.get("cache-control");
    const cachePutDate = headers.get("cache-put-date");
    if (!cacheControl || !cachePutDate) return cacheInfo;

    return (
      cacheControl
        .toLocaleLowerCase()
        .split(",")
        .map((value) => value.trim())
        .reduce((cacheInfo, value) => {
          if (value.startsWith("max-age=")) {
            const maxAgeMillis = parseInt(value.split("=").pop() ?? "0") * 1000;
            return {
              ...cacheInfo,
              stale:
                Date.now() - new Date(cachePutDate).getTime() > maxAgeMillis,
            };
          }
          if (value.startsWith("stale-while-revalidate=")) {
            const maxAgeMillis = parseInt(value.split("=").pop() ?? "0") * 1000;
            return {
              ...cacheInfo,
              revalidate:
                Date.now() - new Date(cachePutDate).getTime() < maxAgeMillis,
            };
          }
          return cacheInfo;
        }, cacheInfo) ?? cacheInfo
    );
  }

  hashKey(queryKey: string | readonly unknown[]): string {
    const rawKeys = Array.isArray(queryKey) ? queryKey : [queryKey];
    let hash = "";

    for (const key of rawKeys) {
      if (key != null) {
        if (typeof key === "object") {
          hash += JSON.stringify(key);
        } else {
          hash += key.toString();
        }
      }
    }

    return encodeURIComponent(hash);
  }

  async clear(force = false) {
    if (this.#cachePromise) {
      await this.#cachePromise;
    }

    if (!this.#cache) return;

    this.#debug("Clear cache", { name: this.#name, force });

    if (force) {
      this.#pending.forEach((request) => {
        this.#debug("Abort pending request", { name: this.#name });
        request.controller.abort("Forced cache clear");
      });
      this.#pending.clear();
    }

    const keys = await this.#cache.keys();
    for (const key of keys) {
      await this.#cache.delete(key);
      this.#debug("Deleted from cache", { name: this.#name, key });
    }
  }
}
